'use client';

/**
 * useUserNotifications Hook (WEB-432)
 *
 * Subscribes to the authenticated user's `userNotifications` GraphQL
 * subscription (REA-32) so the notification bell, panel, and toasts stay in
 * sync across tabs, devices, reloads, and offline gaps.
 *
 * ─── NON-NEGOTIABLE INVARIANT ────────────────────────────────────────────────
 * The WS frame is a "something changed" HINT. On every frame this hook calls
 * `lociApi.util.invalidateTags(['Notifications'])`, which refetches
 * `getUnreadCount` (the SOLE authoritative count) and `getNotifications` (the
 * list). It NEVER increments a local counter and NEVER reads a count off the WS
 * payload. This is what stops the bell lying across surfaces.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Side effects:
 *  - Invalidates the `Notifications` cache tag on every frame so every active
 *    `useGetUnreadCountQuery` / `useGetNotificationsQuery` consumer refetches.
 *  - Forwards each payload to an optional `onNotification` callback (used by a
 *    consumer to raise a toast for high-priority frames).
 *
 * Mirrors `useSavedEventChanged` (REA-17) EXACTLY for the `getWsClient()`
 * lifecycle, SSR-safety, and tag-invalidation pattern so the realtime-hook
 * family stays consistent.
 *
 * The `userNotifications` subscription takes NO arguments — it is keyed off the
 * verified JWT subject in the WebSocket connection context (the server only
 * ever delivers the subscriber's own notifications). We still gate on `userId`
 * presence so the hook no-ops while signed out, matching the sibling hooks.
 */

import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { lociApi } from '@loci/api';
import { logger } from '@/lib/logger';
import {
  closeWsClient,
  getWsClient,
  reconnectWsClient,
  setConnectionParamsGetter,
} from '@/lib/wsClient';

/**
 * Kind of user notification. Mirrors the `UserNotificationKind` enum in
 * `apps/ws-subscription-server/src/schema.graphql` and the canonical
 * `UserNotificationKindSchema` in `@loci/realtime-types`.
 */
export type UserNotificationKind =
  'EVENT_REMINDER' | 'TICKET_DROP' | 'PRICE_CHANGE' | 'SAVED_EVENT_UPDATE' | 'SYSTEM';

/**
 * Payload delivered on the `userNotifications` subscription. Inlined here to
 * avoid pulling `@loci/realtime-types` into the web app's runtime dependency
 * graph (same convention as `useSavedEventChanged`).
 * The server validates with the canonical `UserNotificationSchema` Zod schema
 * before publish, so this type is the boundary contract only.
 *
 * `metadata` arrives as a JSON-encoded string over the GraphQL wire (the
 * resolver serializes the underlying `Record<string, unknown>`), or `null`.
 *
 * `severity` is carried end-to-end as of WEB-435: the backend publisher
 * projection (`services/backend-api/src/utils/websocket-publisher.ts`) now
 * populates it from the persisted `Notification.severity` column and the
 * GraphQL `UserNotification` type exposes it. Consumers that want to raise a
 * toast only for high-priority frames filter inside their own `onNotification`
 * callback — this hook never inspects severity itself. It remains optional on
 * this boundary type so a frame from an older server (pre-WEB-435) still
 * type-checks; treat an absent severity as `normal`.
 */
export interface UserNotification {
  id: string;
  userId: string;
  kind: UserNotificationKind;
  title: string;
  body?: string | null;
  url?: string | null;
  metadata?: string | null;
  createdAt: string;
  /** `high` is the toast trigger; absent (older servers) ⇒ treat as `normal`. */
  severity?: 'normal' | 'high';
}

export interface UseUserNotificationsOptions {
  /** Authenticated user id. The hook no-ops while empty (e.g. signed-out). */
  userId: string;
  /**
   * CMS route (`/admin/*`). Empty `userId` is shared with signed-out consumer
   * pages, so this flag is a separate effect dep: NotificationToastBridge
   * stays mounted across client nav, and empty→empty `/admin` must still
   * dispose the cached graphql-ws client (WEB-957 / WEB-372).
   * When omitted, the dispose branch falls back to `window.location.pathname`.
   */
  onAdmin?: boolean;
  /**
   * Invoked with each subscription payload. The latest reference is captured in
   * a ref so callers can pass inline functions without forcing the underlying
   * WebSocket to reconnect. Consumers decide whether a given frame warrants a
   * toast (e.g. `if (n.severity === 'high') showNotification(...)`).
   */
  onNotification?: (payload: UserNotification) => void;
  /**
   * In-memory access token for the shared graphql-ws connection. Remaining
   * channels (`userNotifications`, `savedEventChanged`)
   * authenticate through this singleton. REA-40 moved the setter here when
   * `userPreferencesUpdated` was unmounted. A change closes the live socket
   * so graphql-ws replays those subscriptions on a new handshake.
   */
  accessToken?: string | null;
}

export interface UseUserNotificationsResult {
  isConnected: boolean;
  /** The most recent notification payload, or null until the first delivery. */
  lastNotification: UserNotification | null;
}

interface UserNotificationsData {
  userNotifications: UserNotification;
}

// No-arg subscription — the server keys delivery off the verified JWT subject
// in the connection context, never a client-supplied argument.
const SUBSCRIPTION_QUERY = `
  subscription UserNotifications {
    userNotifications {
      id
      userId
      kind
      title
      body
      url
      metadata
      severity
      createdAt
    }
  }
`;

/**
 * Subscribe to `userNotifications` for the authenticated user.
 *
 * SSR-safe: returns disconnected state when there's no browser WebSocket client
 * (server render, missing `NEXT_PUBLIC_WS_URL`, etc.). The unread count + list
 * still work via REST in that case — only the live refetch trigger is absent.
 * Cleans up its subscription on unmount and resubscribes when `userId` changes.
 */
export function useUserNotifications({
  userId,
  onNotification,
  onAdmin,
  accessToken,
}: UseUserNotificationsOptions): UseUserNotificationsResult {
  // Set during render, before descendant subscription effects open the socket.
  setConnectionParamsGetter(() =>
    userId && accessToken ? { authorization: `Bearer ${accessToken}` } : {},
  );

  const dispatch = useDispatch();
  const [isConnected, setIsConnected] = useState(false);
  const [lastNotification, setLastNotification] = useState<UserNotification | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;
  // null until the first effect, so the initial handshake is not torn down.
  const bearerIdentityRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const nextIdentity = userId && accessToken ? `${userId}\0${accessToken}` : '';
    const previousIdentity = bearerIdentityRef.current;
    bearerIdentityRef.current = nextIdentity;
    if (previousIdentity === null || previousIdentity === nextIdentity) {
      return;
    }
    // Login, sign-out, account switch, or access-token refresh. The getter
    // above is already the new identity. Close the socket only — graphql-ws
    // retries and replays every active subscription, including siblings.
    reconnectWsClient();
  }, [userId, accessToken]);

  useEffect(() => {
    // SSR guard: only run in browser.
    if (typeof window === 'undefined') {
      return;
    }

    if (!userId) {
      // Clear before the signed-out exit so the previous user's auth-scoped
      // notification can't survive sign-out / account-switch transitions.
      setLastNotification(null);
      setIsConnected(false);
      // WEB-957 / WEB-955: NotificationToastBridge keeps this hook mounted
      // across client nav and passes an empty userId on `/admin`. Dispose
      // here so the connection layer stays internal (WEB-372) and a cached
      // graphql-ws client cannot reconnect from the CMS. Signed-out on a
      // consumer page must not close the shared client — sibling WS hooks
      // still use it. `onAdmin` is a separate dep so empty→empty nav still
      // re-runs; when omitted, fall back to the current pathname.
      const disposeOnAdmin = onAdmin ?? window.location.pathname.startsWith('/admin');
      if (disposeOnAdmin) {
        closeWsClient();
      }
      return;
    }

    // Reset on userId change before any early exit so a stale notification from
    // the previous user can't bleed into the new subscription's surface (and so
    // auth-scoped data doesn't survive when WS is unavailable).
    setLastNotification(null);

    // Auth can publish a user id before the in-memory access token exists.
    // Subscribing then opens `userNotifications` with `{}`; the server rejects
    // it and graphql-ws ends that operation. A later token only replays
    // subscriptions that are still active, so wait and resubscribe when the
    // token arrives. Sibling channels stay up — this return does not dispose.
    if (!accessToken) {
      setIsConnected(false);
      return;
    }

    // SSR-safe / no-WS-URL: getWsClient() returns null when there's no browser
    // client (e.g. NEXT_PUBLIC_WS_URL absent). REST queries still work.
    const client = getWsClient();
    if (!client) {
      setIsConnected(false);
      return;
    }

    const unsubscribe = client.subscribe(
      {
        query: SUBSCRIPTION_QUERY,
      },
      {
        next: (data: { data?: UserNotificationsData }) => {
          const notification = data.data?.userNotifications;
          if (!notification) return;

          logger.debug('userNotifications received', { notification });

          setLastNotification(notification);

          // INVARIANT: the frame is a refetch HINT only. Invalidate the
          // `Notifications` tag so every active `useGetUnreadCountQuery` and
          // `useGetNotificationsQuery` consumer refetches from the backend.
          // We never increment a local count or read a count off this payload.
          dispatch(lociApi.util.invalidateTags(['Notifications']));

          onNotificationRef.current?.(notification);
        },
        error: (error) => {
          logger.error('userNotifications subscription error:', { error });
          setIsConnected(false);
          if (unsubscribeRef.current) {
            unsubscribeRef.current = null;
          }
        },
        complete: () => {
          setIsConnected(false);
          if (unsubscribeRef.current) {
            unsubscribeRef.current = null;
          }
        },
      },
    );

    setIsConnected(true);
    unsubscribeRef.current = unsubscribe;

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      setIsConnected(false);
    };
  }, [userId, accessToken, dispatch, onAdmin]);

  return {
    isConnected,
    lastNotification,
  };
}
